import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, concatMap, exhaustMap, map, mapTo, mergeMap } from 'rxjs/operators';
import { NotificationType } from '@app/core/state/core.model';
import { ValidateService } from '../services/validate.service';
import {
  validate,
  validateSuccess,
  validateFailure,
  validateLink,
  validateLinkSuccess,
  validateLinkFailure
} from './validate.actions';
import {
  authRedirectAfterLogin,
  authRedirectToLogin,
  authSetIdentityAndBootup,
  authSetIdentityWithoutBooting,
  authSetIdentityWithoutBootingAndGenerateSecret,
} from '@app/auth/core/state/auth.actions';
import { notificationSend } from '@app/core/state/core.actions';
import { Identity } from '@app/auth/models';

@Injectable()
export class ValidateEffects {
  validate$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(validate),
      map(action => action.payload),
      exhaustMap(payload => {
        return this.validateService.validate$(payload).pipe(
          mergeMap((resp: Identity) => {
            if (resp.status === 'needs_2fa_code_verification') {
              return [authSetIdentityWithoutBooting(resp)];
            }

            if (resp.status === 'needs_2fa_setup') {
              return [authSetIdentityWithoutBootingAndGenerateSecret(resp)];
            }

            if (resp.status === 'authenticated') {
              return [
                validateSuccess(),
                authSetIdentityAndBootup(resp),
                authRedirectAfterLogin({ path: '' }),
              ];
            }

            return [validateFailure({ payload: { message: 'status.invalid' } })];
          }),
          catchError((httpError: HttpErrorResponse) => {
            return of(
              validateFailure({
                payload: {
                  code: httpError.error.code,
                  message: httpError.error.message || httpError.error,
                  httpStatus: httpError.status,
                }
              }),
            );
          }),
        );
      }),
    );
  });

  validateSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(validateSuccess),
      mapTo(
        notificationSend({
          message: $localize`:@@ts.validate.pass.success:Password saved`,
          notificationType: NotificationType.SUCCESS,
        }),
      ),
    );
  });

  validateFailure$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(validateFailure),
      map(action => action.payload),
      concatMap(payload => {
        if (payload.code === 'invalid.operation') {
          return [
            notificationSend({
              message: $localize`:@@ts.validate.email.invalid:Email already verified`,
              notificationType: NotificationType.ERROR,
            }),
            authRedirectToLogin({ path: window.location.pathname }),
          ];
        }

        return [];
      }),
    );
  });

  validateLink$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(validateLink),
      map(action => action.payload),
      exhaustMap(payload => {
        return this.validateService.validateLink$(payload).pipe(
          map((resp: Identity) => {
            return validateLinkSuccess();
          }),
          catchError((httpError: HttpErrorResponse) => {
            return of(
              validateLinkFailure({
                payload: {
                  code: httpError.error.code,
                  message: httpError.error.message || httpError.error,
                  httpStatus: httpError.status,
                }
              }),
            );
          }),
        );
      }),
    );
  });

  constructor(private actions$: Actions, private validateService: ValidateService) {}
}
